/**
 * Calendar-date helpers.
 *
 * PantryMind stores dates as plain ISO calendar dates ("YYYY-MM-DD"), never
 * timestamps — food expires on a day, not at an instant.
 *
 * Arithmetic happens in UTC even though the dates are local, because UTC days
 * are always exactly 24 hours. Doing it in local time would need special cases
 * for the two days a year that are 23 or 25 hours long.
 */

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

/**
 * Converts "YYYY-MM-DD" to UTC-midnight milliseconds, or null if it isn't a
 * real date.
 */
function toUtcMillis(iso: string): number | null {
  if (!ISO_DATE_PATTERN.test(iso)) return null;

  const [year, month, day] = iso.split("-").map(Number);
  const millis = Date.UTC(year, month - 1, day);

  // Date.UTC silently rolls overflow dates forward ("2026-02-31" becomes March)
  // and maps two-digit years into the 1900s. Formatting it back and comparing
  // catches both.
  return formatUtc(millis) === iso ? millis : null;
}

function formatUtc(millis: number): string {
  return new Date(millis).toISOString().slice(0, 10);
}

/** True if the string is a real calendar date in "YYYY-MM-DD" form. */
export function isValidIsoDate(iso: string): boolean {
  return toUtcMillis(iso) !== null;
}

/** Today, from the user's own clock. */
export function todayIso(): string {
  const now = new Date();
  // Built from local parts on purpose: toISOString() would return tomorrow's
  // date for anyone west of Greenwich during their evening.
  const year = String(now.getFullYear()).padStart(4, "0");
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Adds whole days to an ISO date. Throws on invalid input rather than returning junk. */
export function addDays(iso: string, days: number): string {
  const millis = toUtcMillis(iso);
  if (millis === null) throw new Error(`addDays: invalid ISO date "${iso}"`);

  return formatUtc(millis + days * MS_PER_DAY);
}

/**
 * Whole days from one date to another. Negative when `to` is earlier.
 * Exact, not rounded: both sides are UTC midnights, so the gap is always a
 * whole number of 24-hour days.
 */
export function daysBetween(from: string, to: string): number {
  const start = toUtcMillis(from);
  const end = toUtcMillis(to);
  if (start === null || end === null) {
    throw new Error(`daysBetween: invalid ISO date ("${from}", "${to}")`);
  }

  return (end - start) / MS_PER_DAY;
}
